<template>
  <div class="supplier-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('supplierManage.page.title')" :subtitle="$t('supplierManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-select v-model="conditions.stId" clearable class="w-full"
            :placeholder="$t('supplierManage.pleaseSelect') + $t('supplierManage.supplierType')">
            <el-option v-for="item in supplierTypes" :key="item.stId" :label="item.typeName" :value="item.stId" />
          </el-select>
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.supplierName" clearable
            :placeholder="$t('supplierManage.pleaseSelect') + $t('supplierManage.supplierName')"
            @keyup.enter.native="querySupplierMethod" />
        </el-col>
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.personName" clearable
            :placeholder="$t('supplierManage.pleaseSelect') + $t('supplierManage.contactPerson')"
            @keyup.enter.native="querySupplierMethod" />
        </el-col>
        <el-col v-show="moreCondition" :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="conditions.personTel" clearable
            :placeholder="$t('supplierManage.pleaseSelect') + $t('supplierManage.contactTel')"
            @keyup.enter.native="querySupplierMethod" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="querySupplierMethod">
          {{ $t('supplierManage.query') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="resetSupplierMethod">
          {{ $t('common.reset') }}
        </el-button>
        <el-button type="text" icon="el-icon-more" @click="toggleMoreCondition">
          {{ moreCondition ? $t('supplierManage.hide') : $t('supplierManage.moreCondition') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="listSuppliers">
        <template #left>
          <span class="ds-panel__title">{{ $t('supplierManage.supplierInfo') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="openAddSupplierModal">
            {{ $t('supplierManage.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="suppliers" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('supplierManage.page.empty')" @retry="listSuppliers">
        <el-table-column prop="typeName" :label="$t('supplierManage.supplierType')" align="center" min-width="120" />
        <el-table-column prop="supplierName" :label="$t('supplierManage.supplierName')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="personName" :label="$t('supplierManage.contactPerson')" align="center" width="120" />
        <el-table-column prop="personTel" :label="$t('supplierManage.contactTel')" align="center" width="140">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.personTel || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="startTime" :label="$t('supplierManage.startTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="endTime" :label="$t('supplierManage.endTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.endTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" :label="$t('supplierManage.createTime')" align="center" width="160">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.createTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="remark" :label="$t('supplierManage.remark')" align="center" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.remark || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('supplierManage.operation')" align="center" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="openEditSupplierModel(scope.row)">
              {{ $t('supplierManage.edit') }}
            </el-button>
            <el-button size="mini" type="text" @click="openConfigSupplierModel(scope.row)">
              {{ $t('supplierManage.config') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="openDeleteSupplierModel(scope.row)">
              {{ $t('supplierManage.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <add-supplier ref="addSupplier" @success="listSuppliers" />
    <edit-supplier ref="editSupplier" @success="listSuppliers" />
    <delete-supplier ref="deleteSupplier" @success="listSuppliers" />
    <config-supplier ref="configSupplier" />
  </div>
</template>

<script>
import { listSupplier, listSupplierType } from '@/api/scm/supplierManageApi'
import AddSupplier from '@/components/scm/AddSupplier'
import EditSupplier from '@/components/scm/EditSupplier'
import DeleteSupplier from '@/components/scm/DeleteSupplier'
import ConfigSupplier from '@/components/scm/ConfigSupplier'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'SupplierManageList',
  components: {
    AddSupplier,
    EditSupplier,
    DeleteSupplier,
    ConfigSupplier,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      moreCondition: false,
      conditions: {
        stId: '',
        supplierName: '',
        personName: '',
        personTel: ''
      },
      suppliers: [],
      supplierTypes: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      }
    }
  },
  created() {
    this.listSuppliers()
    this.listSupplierTypes()
  },
  methods: {
    async listSuppliers() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          ...this.conditions
        }
        const res = await listSupplier(params)
        this.suppliers = Array.isArray(res.data) ? res.data : []
        this.page.total = res.total || 0
      } catch (error) {
        console.error('查询供应商列表失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('supplierManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },

    async listSupplierTypes() {
      try {
        const res = await listSupplierType({ page: 1, row: 100 })
        this.supplierTypes = Array.isArray(res.data) ? res.data : []
      } catch (error) {
        console.error('查询供应商类型失败:', error)
      }
    },

    querySupplierMethod() {
      this.page.current = 1
      this.listSuppliers()
    },

    resetSupplierMethod() {
      this.conditions = {
        stId: '',
        supplierName: '',
        personName: '',
        personTel: ''
      }
      this.page.current = 1
      this.listSuppliers()
    },

    toggleMoreCondition() {
      this.moreCondition = !this.moreCondition
    },

    openAddSupplierModal() {
      this.$refs.addSupplier.open()
    },

    openEditSupplierModel(row) {
      this.$refs.editSupplier.open(row)
    },

    openConfigSupplierModel(row) {
      this.$refs.configSupplier.open(row)
    },

    openDeleteSupplierModel(row) {
      this.$refs.deleteSupplier.open(row)
    },

    handleSizeChange(size) {
      this.page.size = size
      this.page.current = 1
      this.listSuppliers()
    },

    handleCurrentChange(current) {
      this.page.current = current
      this.listSuppliers()
    }
  }
}
</script>

<style lang="scss" scoped>
.supplier-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
